<template>
  <section class="invoice-preview-wrapper">
    <!-- Alert: No item found -->
    <b-alert variant="danger" :show="invoiceData === undefined">
      <h4 class="alert-heading">Error fetching invoice data</h4>
      <div class="alert-body">
        No invoice found with this invoice id. Check
        <b-link class="alert-link" :to="{ name: 'apps-invoice-list' }"> Invoice List </b-link>
        for other invoices.
      </div>
    </b-alert>

    <b-row v-if="invoiceData" class="invoice-preview">
      <!-- Col: Left (Invoice Container) -->
      <b-col cols="12" xl="9" md="8">
        <b-overlay :show="isLoading" spinner-variant="primary" spinner-large rounded="sm">
          <b-card no-body class="invoice-preview-card">
            <!-- Header -->
            <b-card-body class="invoice-padding pb-0">
              <div
                class="d-flex justify-content-between flex-md-row flex-column invoice-spacing mt-0"
              >
                <!-- Header: Left Content -->
                <div v-if="customerData">
                  <p class="card-text mb-25">
                    {{ customerData.address || 'Address not available' }}
                  </p>
                  <p class="card-text mb-25">
                    {{ customerData.province || ''
                    }}{{ customerData.province && customerData.gstNumber ? ', GST: ' : ''
                    }}{{ customerData.gstNumber || '' }}
                  </p>
                  <p class="card-text mb-0">
                    {{ customerData.cellNumber || 'Phone not available'
                    }}{{ customerData.cellNumber && customerData.email ? ', ' : ''
                    }}{{ customerData.email || '' }}
                  </p>
                </div>

                <!-- Header: Right Content -->
                <div class="mt-md-0 mt-2">
                  <h4 class="invoice-title">
                    Invoice
                    <span class="invoice-number">#{{ invoiceData.docNo }}</span>
                  </h4>
                  <div class="invoice-date-wrapper">
                    <p class="invoice-date-title">Date Issued:</p>
                    <p class="invoice-date">
                      {{ formatDate(invoiceData.tranDate) }}
                    </p>
                  </div>
                  <div class="invoice-date-wrapper">
                    <p class="invoice-date-title">Sales Type:</p>
                    <p class="invoice-date">
                      {{ invoiceData.metaSalesTypeName }}
                    </p>
                  </div>
                </div>
              </div>
            </b-card-body>

            <!-- Spacer -->
            <hr class="invoice-spacing" />

            <!-- Invoice Customer & Payment Details -->
            <b-card-body v-if="invoiceData && customerData" class="invoice-padding pt-0">
              <b-row class="invoice-spacing">
                <!-- Col: Invoice To -->
                <b-col cols="12" xl="6" class="p-0">
                  <h6 class="mb-2">Invoice To:</h6>
                  <h6 class="mb-25">
                    {{ customerData.name || invoiceData.metaCustomerName }}
                  </h6>
                  <p class="card-text mb-25">
                    {{ customerData.shortName ? `(${customerData.shortName})` : '' }}
                  </p>
                  <p class="card-text mb-25">
                    {{ customerData.address || 'Address not available' }}
                  </p>
                  <p class="card-text mb-25">
                    {{ customerData.province || ''
                    }}{{ customerData.province && customerData.ntnNumber ? ', NTN: ' : ''
                    }}{{ customerData.ntnNumber || '' }}
                  </p>
                  <p class="card-text mb-25">Contact: {{ customerData.contactPerson || 'N/A' }}</p>
                  <p class="card-text mb-25">
                    {{ customerData.cellNumber || 'Phone not available' }}
                  </p>
                  <p class="card-text mb-0">
                    {{ customerData.email || 'Email not available' }}
                  </p>
                </b-col>

                <!-- Col: Payment Details -->
                <b-col xl="6" cols="12" class="p-0 mt-xl-0 mt-2 d-flex justify-content-xl-end">
                  <div>
                    <h6 class="mb-2">Invoice Details:</h6>
                    <table>
                      <tbody>
                        <tr>
                          <td class="pr-1">Gross Amount:</td>
                          <td>
                            <span class="font-weight-bold">{{
                              formatCurrency(invoiceData.grossAmount)
                            }}</span>
                          </td>
                        </tr>
                        <tr>
                          <td class="pr-1">Total Discount:</td>
                          <td>{{ formatCurrency(invoiceData.totalDiscount) }}</td>
                        </tr>
                        <tr>
                          <td class="pr-1">Net Amount:</td>
                          <td>{{ formatCurrency(invoiceData.valWithoutTax) }}</td>
                        </tr>
                        <tr>
                          <td class="pr-1">236G/H Amount:</td>
                          <td>
                            <span class="font-weight-bold">{{
                              formatCurrency(invoiceData.valIncludingTax)
                            }}</span>
                          </td>
                        </tr>
                        <tr>
                          <td class="pr-1">Total Due:</td>
                          <td>
                            <span class="font-weight-bold">{{
                              formatCurrency(invoiceData.netAmount)
                            }}</span>
                          </td>
                        </tr>
                        <tr><td colspan="2"><hr class="my-1" /></td></tr>
                        <tr>
                          <td class="pr-1">Sales Tax:</td>
                          <td>{{ formatCurrency(invoiceData.totalSalesTax) }}</td>
                        </tr>
                        <tr>
                          <td class="pr-1">Further Tax:</td>
                          <td>{{ formatCurrency(invoiceData.totalFurtherTax) }}</td>
                        </tr>
                        <tr>
                          <td class="pr-1">Fixed/Notified:</td>
                          <td>{{ formatCurrency(invoiceData.notifiedValue) }}</td>
                        </tr>                        <tr>
                          <td class="pr-1">Additional Tax:</td>
                          <td>{{ formatCurrency(invoiceData.totalAdvanceTax) }}</td>
                        </tr>
                        <tr>
                          <td class="pr-1">Total Tax:</td>
                          <td><strong>{{ formatCurrency(invoiceData.totalTax) }}</strong></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </b-col>
              </b-row>
            </b-card-body>

            <!-- Invoice Description: Table -->
            <b-table-lite
              responsive
              :items="invoiceDetails"
              :fields="[
                'itemHSCode',
                'qty',
                'rate',
                'grossAmount',
                'discountAmount',
                'netAmount',
                'sroScheduleNo',
                'sroItemSerialNo',
                'notifiedValue',
                'totalTax',
                'valIncludingTax',
              ]"
            >
              <template #head(itemHSCode)="data">
                <span>Item</span>
              </template>
              <template #head(qty)="data">
                <span>Qty</span>
              </template>
              <template #head(rate)="data">
                <span>Rate</span>
              </template>
              <template #head(grossAmount)="data">
                <span>Gross Amount</span>
              </template>
              <template #head(discountAmount)="data">
                <span>Discount</span>
              </template>
              <template #head(netAmount)="data">
                <span>Net Amount</span>
              </template>
              <template #head(sroScheduleNo)="data">
                <span>SRO Schedule No</span>
              </template>
              <template #head(sroItemSerialNo)="data">
                <span>SRO Serial No</span>
              </template>
              <template #head(notifiedValue)="data">
                <span>Fixed/Notified</span>
              </template>
              <template #head(totalTax)="data">
                <span>Total Tax</span>
              </template>
              <template #head(valIncludingTax)="data">
                <span>236G Amount</span>
              </template>

              <template #cell(itemHSCode)="data">
                <b-card-text class="font-weight-bold mb-25">
                  {{ data.item.itemHSCode }}
                </b-card-text>
                <b-card-text class="text-nowrap"> Unit: {{ data.item.metaUnitName }} </b-card-text>
              </template>
              <template #cell(qty)="data">
                {{ data.item.qty }}
              </template>
              <template #cell(rate)="data">
                {{ formatCurrency(data.item.rate) }}
              </template>
              <template #cell(grossAmount)="data">
                {{ formatCurrency(data.item.grossAmount) }}
              </template>
              <template #cell(discountAmount)="data">
                {{ formatCurrency(data.item.discountAmount) }}
              </template>
              <template #cell(netAmount)="data">
                {{ formatCurrency(data.item.netAmount) }}
              </template>
              <template #cell(sroScheduleNo)="data">
                {{ data.item.sroScheduleNo || 'N/A' }}
              </template>
              <template #cell(sroItemSerialNo)="data">
                {{ data.item.sroItemSerialNo || 'N/A' }}
              </template>
              <template #cell(notifiedValue)="data">
                {{ formatCurrency(data.item.notifiedValue) }}
              </template>
              <template #cell(totalTax)="data">
                {{ formatCurrency(data.item.totalTax) }}
              </template>
              <template #cell(valIncludingTax)="data">
                <span class="font-weight-bold">{{
                  formatCurrency(data.item.valIncludingTax)
                }}</span>
              </template>
            </b-table-lite>

            <!-- Invoice Description: Total -->
            <b-card-body class="invoice-padding pb-0">
              <b-row>
                <!-- Col: Invoice Info -->
                <b-col cols="12" md="6" class="mt-md-0 mt-3" order="2" order-md="1">
                  <b-card-text class="mb-0">
                    <span class="font-weight-bold">FBR Invoice No:</span>
                    <span class="ml-75">{{ invoiceData.fbrInvoiceNo || 'N/A' }}</span>
                  </b-card-text>
                  <b-card-text class="mb-0">
                    <span class="font-weight-bold">SRO Schedule No:</span>
                    <span class="ml-75">{{ invoiceData.sroScheduleNo || 'N/A' }}</span>
                  </b-card-text>
                  <b-card-text class="mb-0">
                    <span class="font-weight-bold">Sales Tax Invoice No:</span>
                    <span class="ml-75">{{ invoiceData.salesTaxInvoiceNo || 'N/A' }}</span>
                  </b-card-text>
                </b-col>

                <!-- Col: Total -->
                <b-col
                  cols="12"
                  md="6"
                  class="mt-md-6 d-flex justify-content-end"
                  order="1"
                  order-md="2"
                >
                  <div class="invoice-total-wrapper">
                    <div class="invoice-total-item">
                      <p class="invoice-total-title">Gross Amount:</p>
                      <p class="invoice-total-amount">
                        {{ formatCurrency(invoiceData.grossAmount) }}
                      </p>
                    </div>
                    <div class="invoice-total-item">
                      <p class="invoice-total-title">Total Discount:</p>
                      <p class="invoice-total-amount">
                        {{ formatCurrency(invoiceData.totalDiscount) }}
                      </p>
                    </div>
                    <div class="invoice-total-item">
                      <p class="invoice-total-title">Net Amount:</p>
                      <p class="invoice-total-amount">
                        {{ formatCurrency(invoiceData.valWithoutTax) }}
                      </p>
                    </div>
                    <div class="invoice-total-item">
                      <p class="invoice-total-title">236G Amount:</p>
                      <p class="invoice-total-amount">
                        <strong>{{ formatCurrency(invoiceData.valIncludingTax) }}</strong>
                      </p>
                    </div>
                    <hr class="my-50" />
                    <div class="invoice-total-item">
                      <p class="invoice-total-title">Total Due:</p>
                      <p class="invoice-total-amount">
                        <strong>{{ formatCurrency(invoiceData.netAmount) }}</strong>
                      </p>
                    </div>
                    <hr class="my-50" />
                    <div class="invoice-total-item">
                      <p class="invoice-total-title">Sales Tax:</p>
                      <p class="invoice-total-amount">{{ formatCurrency(invoiceData.totalSalesTax) }}</p>
                    </div>
                    <div class="invoice-total-item">
                      <p class="invoice-total-title">Further Tax:</p>
                      <p class="invoice-total-amount">{{ formatCurrency(invoiceData.totalFurtherTax) }}</p>
                    </div>
                    <!-- <div class="invoice-total-item">
                      <p class="invoice-total-title">Fixed/Notified:</p>
                      <p class="invoice-total-amount">{{ formatCurrency(invoiceData.totalNotifiedValue) }}</p>
                    </div> -->
                    <div class="invoice-total-item">
                      <p class="invoice-total-title">Additional Tax:</p>
                      <p class="invoice-total-amount">{{ formatCurrency(invoiceData.totalAdvanceTax) }}</p>
                    </div>
                    <div class="invoice-total-item">
                      <p class="invoice-total-title">Total Tax:</p>
                      <p class="invoice-total-amount"><strong>{{ formatCurrency(invoiceData.totalTax) }}</strong></p>
                    </div>
                  </div>
                </b-col>
              </b-row>
            </b-card-body>

            <!-- Spacer -->
            <hr class="invoice-spacing" />

            <!-- Note -->
            <b-card-body class="invoice-padding pt-0" v-if="invoiceData.remarks">
              <span class="font-weight-bold">Remarks: </span>
              <span>{{ invoiceData.remarks }}</span>
            </b-card-body>
          </b-card>
        </b-overlay>
      </b-col>

      <!-- Right Col: Card -->
      <b-col cols="12" md="4" xl="3" class="invoice-actions">
        <b-overlay :show="isLoading" spinner-variant="primary" spinner-large rounded="sm">
          <b-card>
            <!-- Button: DOwnload -->
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              variant="outline-secondary"
              class="mb-75"
              block
              :to="{ name: 'sales-invoice' }"
            >
              <feather-icon icon="ArrowLeft" />
              Back
            </b-button>

            <!-- Button: Print -->
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              variant="outline-secondary"
              class="mb-75"
              block
              @click="printInvoice"
            >
              <feather-icon icon="PrinterIcon" size="16" class="mr-50" />
              Print
            </b-button>

            <!-- Button: Edit -->
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              variant="outline-secondary"
              class="mb-75"
              block
              :to="{ name: 'sales-invoice-edit', params: { id: $route.params.id } }"
              v-show="invoiceData.fbrStatus === 'pending' || invoiceData.fbrStatus === null"
            >
              Edit
            </b-button>
          </b-card>
        </b-overlay>
      </b-col>
    </b-row>
  </section>
</template>

<script>
import { ref, onUnmounted } from '@vue/composition-api';
import store from '@/store';
import router from '@/router';
import {
  BRow,
  BCol,
  BCard,
  BCardBody,
  BTableLite,
  BCardText,
  BButton,
  BAlert,
  BLink,
  VBToggle,
  BOverlay,
} from 'bootstrap-vue';
import Logo from '@core/layouts/components/Logo.vue';
import Ripple from 'vue-ripple-directive';
import invoiceStoreModule from '../invoiceStoreModule';
import CustomerStoreModule from '../../customer/CustomerStoreModule';
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';

export default {
  directives: {
    Ripple,
    'b-toggle': VBToggle,
  },
  components: {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BTableLite,
    BCardText,
    BButton,
    BAlert,
    BLink,
    ToastificationContent,
    Logo,
    BOverlay,
  },
  setup() {
    // Sales Invoice Store Module
    const SALES_INVOICE_APP_STORE_MODULE_NAME = 'sales-invoice';

    // Register module
    if (!store.hasModule(SALES_INVOICE_APP_STORE_MODULE_NAME))
      store.registerModule(SALES_INVOICE_APP_STORE_MODULE_NAME, invoiceStoreModule);

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(SALES_INVOICE_APP_STORE_MODULE_NAME))
        store.unregisterModule(SALES_INVOICE_APP_STORE_MODULE_NAME);
    });

    // Customer Store Module
    const CUSTOMER_APP_STORE_MODULE_NAME = 'customer';

    // Register module
    if (!store.hasModule(CUSTOMER_APP_STORE_MODULE_NAME))
      store.registerModule(CUSTOMER_APP_STORE_MODULE_NAME, CustomerStoreModule);

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(CUSTOMER_APP_STORE_MODULE_NAME))
        store.unregisterModule(CUSTOMER_APP_STORE_MODULE_NAME);
    });

    const toast = useToast();

    // Initialize reactive data
    const isLoading = ref(true);
    const invoiceData = ref(null);
    const invoiceDetails = ref([]);
    const customerData = ref(null);

    // Fetch invoice data
    store
      .dispatch('sales-invoice/fetchInvoice', { id: router.currentRoute.params.id })
      .then((response) => {
        const { details, entity } = response;

        const correctedDetails = details.map((item) => {
          const valWithoutTax = parseFloat(item.valWithoutTax || 0);
          const totalTax = parseFloat(item.totalTax || 0);
          const tax236GValue = parseFloat(item.tax236GValue || 0);
          const valIncludingTax = parseFloat((((valWithoutTax + totalTax) * tax236GValue) / 100).toFixed(2));
          return { ...item, valIncludingTax };
        });

        const totalAdvanceTax = correctedDetails.reduce((sum, item) => sum + item.valIncludingTax, 0);

        entity.valIncludingTax = totalAdvanceTax;
        entity.totalAdvanceTax = totalAdvanceTax;
        entity.netAmount = (entity.valWithoutTax || 0) + (entity.totalTax || 0) + totalAdvanceTax;

        invoiceData.value = entity;
        invoiceDetails.value = correctedDetails;

        store
          .dispatch('customer/fetchCustomer', { id: entity.customerID })
          .then((response) => {
            customerData.value = response;
          })
          .catch((error) => {
            console.error(error);
            toast({
              component: ToastificationContent,
              props: {
                title: 'Error',
                text: 'Error fetching customer data',
                variant: 'danger',
                icon: 'X',
                solid: true,
              },
            });
          })
          .finally(() => {
            isLoading.value = false;
          });
      })
      .catch((error) => {
        if (error.response.status === 404) {
          invoiceData.value = undefined;
        }
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Error fetching invoice data',
            variant: 'danger',
            solid: true,
            icon: 'X',
          },
        });
      });

    const printInvoice = () => {
      const invoiceId = router.currentRoute.params.id;
      const routeData = router.resolve({ name: 'sales-invoice-print', params: { id: invoiceId } });
      window.open(routeData.href, '_blank');
    };

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    };

    const formatCurrency = (amount) => {
      if (amount === null || amount === undefined) return '0.00';
      return new Intl.NumberFormat('en-PK', {
        style: 'currency',
        currency: 'PKR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    };

    return {
      isLoading,
      invoiceData,
      invoiceDetails,
      customerData,

      printInvoice,
      formatDate,
      formatCurrency,
    };
  },
};
</script>

<style lang="scss" scoped>
@import '~@core/scss/base/pages/app-invoice.scss';
</style>

<style lang="scss">
@media print {
  // Global Styles
  body {
    background-color: transparent !important;
  }
  nav.header-navbar {
    display: none;
  }
  .main-menu {
    display: none;
  }
  .header-navbar-shadow {
    display: none !important;
  }
  .content.app-content {
    margin-left: 0;
    padding-top: 2rem !important;
  }
  footer.footer {
    display: none;
  }
  .card {
    background-color: transparent;
    box-shadow: none;
  }
  .customizer-toggle {
    display: none !important;
  }

  // Invoice Specific Styles
  .invoice-preview-wrapper {
    .row.invoice-preview {
      .col-md-8 {
        max-width: 100%;
        flex-grow: 1;
      }

      .invoice-preview-card {
        .card-body:nth-of-type(2) {
          .row {
            > .col-12 {
              max-width: 50% !important;
            }

            .col-12:nth-child(2) {
              display: flex;
              align-items: flex-start;
              justify-content: flex-end;
              margin-top: 0 !important;
            }
          }
        }
      }
    }

    // Action Right Col
    .invoice-actions {
      display: none;
    }
  }
}
</style>
